// Canonical price block used everywhere prices appear on the site.
//
// Renders the 4-line brand-mandated structure:
//   1. Room type          (bold serif)
//   2. Occupancy line     ("Ocupación: 2 personas" · card variant)
//                         ("Basado en una ocupación de 2 personas" · modal variant)
//   3. From X€ per person and night   (headline amount)
//   4. Board basis included           (localized label)
//
// This is the single source of truth for how a room price surfaces
// across cards, hotel pages, carousels, price modals, the booking
// widget, reservation summaries, comparison tables and search results.
// Every consumer should reach for this component before hand-rolling
// their own layout.

import React from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useFormatPrice } from "@/contexts/CurrencyContext";
import {
  BOARD_BASIS_INCLUDED_LABELS,
  pickOccupancyLabel,
  pickOccupancyBasisLabel,
  pickPerPersonPerNightLabel,
  pickFromLabel,
} from "@/lib/pricingDefaults";

const pick = (obj, lang) => {
  if (!obj) return "";
  if (typeof obj === "string") return obj;
  return obj[lang] || obj.en || obj.fr || "";
};

// Normalise the caller's board basis key to the canonical dictionary.
// Accepts every alias the codebase used before this refactor.
const canonicalBoard = (key) => {
  if (!key) return "room_only";
  const k = String(key).toLowerCase();
  if (k === "bb" || k === "breakfast") return "bb";
  if (k === "hb" || k === "half_board" || k === "half-board") return "hb";
  if (k === "fb" || k === "full_board" || k === "full-board") return "fb";
  return "room_only";
};

// Layout variants:
//   card    — used on room cards and carousels (compact)
//   detail  — used on room detail pages and hotel pages (full column)
//   summary — used inside booking summaries + sticky sidebars (dense)
//   modal   — used inside price modals / drawers (occupancy → basis phrasing)
//   inline  — used inside table rows or booking widgets (single-line)
//   dark    — palette swap for dark backgrounds; combine with variant
//
// `boardBasis` accepts room_only|bb|breakfast|hb|half_board|fb|full_board.
// `amount` is in the same currency the site is currently displaying, formatted
// externally (e.g. via useFormatPrice.fromMAD). Pass the raw MAD value in
// `amountMAD` if you want the block to format it itself using the active
// currency — either shape works.
const RoomPriceBlock = ({
  roomName,
  occupancy,
  amount,
  amountMAD,
  boardBasis = "room_only",
  variant = "card",
  showFromPrefix = true,
  testid = "room-price-block",
  className = "",
  dark = false,
}) => {
  const { lang } = useLanguage();
  const { fromMAD } = useFormatPrice();

  const board = canonicalBoard(boardBasis);
  const boardLabel = pick(BOARD_BASIS_INCLUDED_LABELS[board], lang);
  const occN = Math.max(1, parseInt(occupancy, 10) || 2);
  const isModal = variant === "modal" || variant === "summary";
  const occLine = isModal ? pickOccupancyBasisLabel(occN, lang) : pickOccupancyLabel(occN, lang);
  const perLine = pickPerPersonPerNightLabel(lang);
  const fromWord = pickFromLabel(lang);

  const formatted =
    amount != null
      ? amount
      : amountMAD != null && !Number.isNaN(Number(amountMAD))
      ? fromMAD(Number(amountMAD))
      : null;

  if (!formatted) return null;

  // Palette
  const cName = dark ? "text-[#FDFBF7]" : "text-[#1A1513]";
  const cMuted = dark ? "text-[#FDFBF7]/70" : "text-[#5C5248]";
  const cAmount = dark ? "text-[#D4A373]" : "text-[#C16542]";
  const cSmall = dark ? "text-[#FDFBF7]/60" : "text-[#5C5248]";

  // Sizing
  const isInline = variant === "inline";
  const isDetail = variant === "detail";
  const nameSize = isInline
    ? "text-sm"
    : isDetail
    ? "font-serif-x text-xl md:text-2xl tracking-tight"
    : "font-serif-x text-lg tracking-tight";
  const amountSize = isInline
    ? "text-lg"
    : isDetail
    ? "font-serif-x text-3xl md:text-4xl tracking-tight tabular-nums"
    : "font-serif-x text-2xl md:text-[28px] tracking-tight tabular-nums";

  return (
    <div
      className={`flex flex-col ${isInline ? "gap-0.5" : "gap-1.5"} ${className}`}
      data-testid={testid}
    >
      {roomName ? (
        <div className={`${nameSize} ${cName}`} data-testid={`${testid}-name`}>
          {pick(roomName, lang)}
        </div>
      ) : null}

      <div
        className={`text-[11px] tracking-[0.18em] uppercase ${cMuted}`}
        data-testid={`${testid}-occupancy`}
      >
        {occLine}
      </div>

      <div className="flex items-baseline flex-wrap gap-x-2 gap-y-0.5" data-testid={`${testid}-amount-row`}>
        {showFromPrefix ? (
          <span className={`text-[10px] tracking-[0.25em] uppercase ${cMuted}`}>
            {fromWord}
          </span>
        ) : null}
        <span className={`${amountSize} ${cAmount} leading-none`} data-testid={`${testid}-amount`}>
          {formatted}
        </span>
        <span className={`text-xs tracking-wide ${cSmall}`} data-testid={`${testid}-per-line`}>
          {perLine}
        </span>
      </div>

      <div
        className={`text-[11px] tracking-[0.18em] uppercase ${cMuted}`}
        data-testid={`${testid}-board`}
      >
        {boardLabel}
      </div>
    </div>
  );
};

export default RoomPriceBlock;
